/**
 * The page's copy of the server's clock (PROTOCOL.md 6.100.0, ADR-141's amendment of 2026-10-31, P2.23-T13): everyone shares
 * one game time, so the light, the sky and the weather read the server's time and never the player's wall clock. The
 * `welcome` carries the server's time; the clock keeps it against a monotonic reading taken as the welcome arrived and
 * carries it forward by the monotonic clock, so neither a wrong wall clock nor one that is changed mid-session moves it.
 * A frame spends time in flight and in the page's queue before it is handled, which makes the page's reading of the server
 * late; the sample of least latency is the one with the largest offset (server time minus monotonic time), so the clock
 * keeps that one and a later, slower sample leaves it as it was, until the page forgets it.
 */
export class ServerClock {
  private serverAt: number | null = null;
  private monotonicAt = 0;

  /**
   * Takes the server's time from a `welcome`, unless an earlier sample reached the page with less latency.
   *
   * @param serverTime The welcome's server time in milliseconds since the Unix epoch, or undefined when it carried none.
   * @param monotonic The page's monotonic clock in milliseconds as the welcome arrived.
   */
  public note(serverTime: number | undefined, monotonic: number): void {
    if (serverTime !== undefined && (this.serverAt === null || serverTime - monotonic > this.serverAt - this.monotonicAt)) {
      this.serverAt = serverTime;
      this.monotonicAt = monotonic;
    }
  }

  /**
   * Drops the held sample, so the next time a `welcome` carries is taken whatever its latency: the clock of a page that has
   * not been told the time yet. A login or door `welcome` calls it before noting, so a reconnect to a host whose clock is
   * behind the old one is followed; only a seamless crossing keeps the sample it holds.
   */
  public forget(): void {
    this.serverAt = null;
    this.monotonicAt = 0;
  }

  /**
   * The server's time now.
   *
   * @param monotonic The page's monotonic clock in milliseconds now.
   * @returns Milliseconds since the Unix epoch, or null before a welcome has told the page the time.
   */
  public now(monotonic: number): number | null {
    return this.serverAt === null ? null : this.serverAt + (monotonic - this.monotonicAt);
  }
}
